import React, { useEffect, useState } from 'react';
import { useBrandThemeContext } from '@/context/BrandThemeContext';
import { resolveTenantBranding } from '@/utils/tenantBranding';

interface BrandLogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'icon' | 'white';
  showTagline?: boolean;
  className?: string;
}

function LogoImage({
  src,
  alt,
  className,
  fallback,
}: {
  src: string;
  alt: string;
  className?: string;
  fallback: React.ReactNode;
}) {
  const [failed, setFailed] = useState(false);

  if (failed || !src || src === '/favicon.ico') {
    return <>{fallback}</>;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
    />
  );
}

const BrandLogo: React.FC<BrandLogoProps> = ({
  size = 'md',
  variant = 'default',
  className = '',
}) => {
  const { currentBrand, theme } = useBrandThemeContext();
  const tenant = resolveTenantBranding();

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('🎨 BrandLogo render:', { currentBrand, theme: theme?.name, logoSrc: tenant.logoSrc });
    }
  }, [currentBrand, theme, tenant.logoSrc]);

  const isWhite = variant === 'white';

  const getSizeClasses = () => {
    const isCoBranded = tenant.brandKey === 'ing' || tenant.brandKey === 'bnp';
    switch (size) {
      case 'sm':
        return {
          container: 'h-8 max-h-8',
          icon: `h-8 w-auto ${isCoBranded ? 'max-w-[min(100%,280px)]' : 'max-w-[200px]'}`,
        };
      case 'md':
        return {
          container: 'h-12 max-h-12',
          icon: `h-12 w-auto ${isCoBranded ? 'max-w-[min(100%,360px)]' : 'max-w-[260px]'}`,
        };
      case 'lg':
        return {
          container: 'h-16 max-h-16',
          icon: `h-16 w-auto ${isCoBranded ? 'max-w-[min(100%,480px)]' : 'max-w-[320px]'}`,
        };
      case 'xl':
        return {
          container: 'h-20 max-h-20',
          icon: `h-20 w-auto ${isCoBranded ? 'max-w-[min(100%,560px)]' : 'max-w-[400px]'}`,
        };
      default:
        return {
          container: 'h-12 max-h-12',
          icon: `h-12 w-auto ${isCoBranded ? 'max-w-[min(100%,360px)]' : 'max-w-[260px]'}`,
        };
    }
  };

  const sizeClasses = getSizeClasses();
  const logoSrc = tenant.logoSrc || theme?.logoUrl || '/favicon.ico';
  const alt = tenant.displayName || theme?.companyName || 'Brand';

  const RBKoFallback = () => (
    <svg viewBox="0 0 120 40" className={sizeClasses.icon} aria-hidden="true">
      <rect width="120" height="40" rx="4" fill="#FFEB3B" />
      <text x="60" y="26" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#000">
        RBKo
      </text>
    </svg>
  );

  const VaiuFallback = () => (
    <svg viewBox="0 0 260 62" className={sizeClasses.icon} aria-hidden="true">
      <rect width="260" height="62" rx="4" fill="none" />
      <path
        d="M8 8 L28 54 L48 8"
        fill="none"
        stroke={isWhite ? '#ffffff' : '#E85D4C'}
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <text x="58" y="42" fontSize="28" fontWeight="bold" fill={isWhite ? '#ffffff' : '#E85D4C'}>
        Vaiu
      </text>
      <line x1="138" y1="14" x2="138" y2="48" stroke="#999" strokeWidth="2" />
      <text x="150" y="28" fontSize="12" fill="#666">
        Tokenized
      </text>
      <text x="150" y="48" fontSize="12" fill="#666">
        Pay
      </text>
    </svg>
  );

  const BNPFallback = () => (
    <svg viewBox="0 0 352 62" className={sizeClasses.icon} aria-hidden="true">
      <rect x="0" y="6" width="50" height="50" rx="6" fill="#009A44" />
      <text x="70" y="42" fontSize="14" fill="#666">
        + Vaiu | Tokenized Pay
      </text>
    </svg>
  );

  const INGFallback = () => (
    <svg viewBox="0 0 479 62" className={sizeClasses.icon} aria-hidden="true">
      <text x="8" y="44" fontSize="32" fontWeight="bold" fill="#003087">
        ING
      </text>
      <text x="90" y="42" fontSize="14" fill="#666">
        + Vaiu | Tokenized Pay
      </text>
    </svg>
  );

  const PayForMyFoodFallback = () => (
    <svg viewBox="0 0 100 100" className={`${sizeClasses.icon} aspect-square`} aria-hidden="true">
      <circle
        cx="50"
        cy="50"
        r="45"
        fill={isWhite ? '#ffffff' : theme.brandColors.primary}
        stroke={isWhite ? '#e0e0e0' : theme.brandColors.secondary}
        strokeWidth="4"
      />
      <text x="50" y="55" textAnchor="middle" fontSize="11" fontWeight="bold" fill={isWhite ? theme.brandColors.primary : '#fff'}>
        PAY
      </text>
    </svg>
  );

  const fallbackForBrand = () => {
    switch (currentBrand) {
      case 'rbko':
        return <RBKoFallback />;
      case 'bnp':
        return <BNPFallback />;
      case 'ing':
        return <INGFallback />;
      case 'payformyfood':
        return <PayForMyFoodFallback />;
      case 'vaiu':
      case 'clean':
      default:
        return <VaiuFallback />;
    }
  };

  return (
    <div className={`flex items-center justify-center ${sizeClasses.container} ${className}`}>
      <LogoImage
        src={logoSrc}
        alt={alt}
        className={`${sizeClasses.icon} object-contain object-center`}
        fallback={fallbackForBrand()}
      />
    </div>
  );
};

export default BrandLogo;
